import React, { useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { ChevronLeft, FileText, Image as ImageIcon, Calendar, Hash, Eye, Sparkles, Euro, FileCheck, ListChecks, AlertOctagon, AlertTriangle, Archive, Trash2 } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { useI18n } from "@/i18n/I18nContext";
import { StatusBadge, CASE_STATUSES } from "@/components/StatusBadge";
import { PriorityBadge } from "@/components/PriorityBadge";
import { BottomNav } from "@/components/BottomNav";
import { format, parseISO } from "date-fns";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogFooter,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogAction,
  AlertDialogCancel,
} from "@/components/ui/alert-dialog";
import { DocumentPreview } from "@/components/DocumentPreview";
import { CaseWorkspace } from "@/components/CaseWorkspace";
import { ResolutionAssistant } from "@/components/ResolutionAssistant";
import { CaseChat } from "@/components/CaseChat";

const KI_ICON = {
  deadline: Calendar,
  amount: Euro,
  reference_number: Hash,
  required_document: FileCheck,
  requested_action: ListChecks,
  consequence: AlertOctagon,
};
const KI_STATUS = {
  PENDING: { bg: "#F1F3F2", c: "#5A625E" },
  CONFIRMED: { bg: "#EDF7F2", c: "#1B6943" },
  CORRECTED: { bg: "#EBF3FF", c: "#1E5299" },
};

export default function CaseDetail() {
  const { t, lang } = useI18n();
  const { id } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [preview, setPreview] = useState(null);
  const [genFollow, setGenFollow] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [searchParams] = useSearchParams();
  const initialTab = searchParams.get("tab");

  const { data: caseData, isLoading, isError } = useQuery({
    queryKey: ["case", id, lang],
    queryFn: async () => (await api.get(`/cases/${id}?language=${lang}`)).data,
    retry: false,
  });

  const { data: followup } = useQuery({
    queryKey: ["followup", id],
    queryFn: async () => (await api.get(`/cases/${id}/followup-status`)).data,
    enabled: !!caseData && caseData.status === "WAITING_FOR_INSTITUTION",
  });

  const generateFollowup = async () => {
    setGenFollow(true);
    try {
      await api.post(`/cases/${id}/drafts`, { type: "FOLLOWUP", ui_language: lang });
      toast.success(t("e4.draftBadge"));
      qc.invalidateQueries({ queryKey: ["drafts", id, "FOLLOWUP"] });
      qc.invalidateQueries({ queryKey: ["events", id] });
    } catch (err) { toast.error(apiError(err, t)); } finally { setGenFollow(false); }
  };

  const updateStatus = async (status) => {
    try {
      await api.patch(`/cases/${id}`, { status });
      toast.success(t("caseDetail.statusUpdated"));
      qc.invalidateQueries({ queryKey: ["case", id] });
      qc.invalidateQueries({ queryKey: ["cases"] });
    } catch (err) {
      toast.error(apiError(err, t));
    }
  };

  const archiveCase = async () => {
    try {
      await api.patch(`/cases/${id}`, { status: "ARCHIVED" });
      toast.success(t("caseDetail.archived"));
      qc.invalidateQueries({ queryKey: ["cases"] });
      navigate("/dashboard");
    } catch (err) {
      toast.error(apiError(err, t));
    }
  };

  const deleteCase = async () => {
    setDeleting(true);
    try {
      await api.delete(`/cases/${id}`);
      toast.success(t("caseDetail.deleted"));
      qc.invalidateQueries({ queryKey: ["cases"] });
      qc.invalidateQueries({ queryKey: ["dashboard"] });
      navigate("/dashboard");
    } catch (err) {
      toast.error(apiError(err, t));
      setDeleting(false);
      setConfirmDelete(false);
    }
  };

  if (isError) {
    return (
      <div className="mx-auto flex min-h-screen max-w-md flex-col items-center justify-center gap-3 bg-[#F8FAF8] px-6 text-center">
        <p className="text-sm text-[#7A9488]">{t("caseDetail.notFound")}</p>
        <button onClick={() => navigate("/dashboard")} className="rounded-full bg-[#2D5A4C] px-4 py-2 text-sm font-semibold text-white">
          {t("common.back")}
        </button>
      </div>
    );
  }

  return (
    <div className="relative mx-auto min-h-screen max-w-md border-x border-[#E2EAE5]/60 bg-[#F8FAF8] pb-28 shadow-sm">
      <header className="sticky top-0 z-40 flex items-center gap-2 border-b border-[#E2EAE5] bg-[#F8FAF8]/90 px-4 py-3 backdrop-blur-md">
        <button data-testid="case-detail-back" onClick={() => navigate("/dashboard")} className="rounded-full p-1.5 text-[#4A6358] hover:bg-[#F0F4F1]">
          <ChevronLeft size={22} />
        </button>
        <h1 className="font-display text-base font-bold text-[#1A2E26]">{t("nav.cases")}</h1>
      </header>

      {isLoading || !caseData ? (
        <div className="space-y-3 px-4 pt-5">
          <div className="h-20 animate-pulse rounded-2xl bg-[#F0F4F1]" />
          <div className="h-32 animate-pulse rounded-2xl bg-[#F0F4F1]" />
        </div>
      ) : (
        <main data-testid="case-detail-view" className="esv-fade px-4 pt-5">
          <div className="rounded-3xl border border-[#E2EAE5] bg-white p-5">
            <div className="flex flex-wrap items-center gap-2">
              <StatusBadge status={caseData.status} />
              <PriorityBadge priority={caseData.priority} />
            </div>
            <h2 className="mt-2 font-display text-xl font-extrabold leading-tight text-[#1A2E26]">{caseData.title}</h2>
            <p className="mt-1 text-sm text-[#7A9488]">{t(`categories.${caseData.category}`, caseData.category)}</p>
            {caseData.priority === "URGENT" && caseData.priority_reason && (
              <div data-testid="case-urgent-reason" className="mt-3 flex items-start gap-2 rounded-xl border border-[#F5C6C6] bg-[#FDECEC] px-3 py-2 text-sm text-[#C0392B]">
                <AlertTriangle size={16} className="mt-0.5 shrink-0" />
                <span dir="auto">{caseData.priority_reason}</span>
              </div>
            )}

            <div className="mt-4 space-y-2 border-t border-[#F0F4F1] pt-4 text-sm">
              {caseData.reference_number && (
                <div className="flex items-center gap-2 text-[#4A6358]">
                  <Hash size={15} className="text-[#7A9488]" />
                  <span className="text-[#7A9488]">{t("caseDetail.reference")}:</span>
                  <span className="font-mono">{caseData.reference_number}</span>
                </div>
              )}
              {caseData.due_date && (
                <div className="flex items-center gap-2 text-[#4A6358]">
                  <Calendar size={15} className="text-[#7A9488]" />
                  <span className="text-[#7A9488]">{t("caseDetail.dueDate")}:</span>
                  <span data-testid="case-detail-duedate">{format(parseISO(caseData.due_date), "dd.MM.yyyy")}</span>
                </div>
              )}
            </div>
          </div>

          {followup?.suggested && (
            <div data-testid="followup-banner" className="mt-5 rounded-2xl border border-[#FFE2B3] bg-[#FFF8EC] p-4">
              <p className="font-display text-sm font-bold text-[#B36B00]">{t("e5.followupTitle")}</p>
              <p className="mt-1 text-xs text-[#8A6A2A]">{t("e5.followupDesc")}</p>
              <button data-testid="followup-generate" onClick={generateFollowup} disabled={genFollow}
                className="mt-3 flex items-center gap-1.5 rounded-full bg-[#2D5A4C] px-3.5 py-2 text-sm font-semibold text-white active:scale-95 disabled:opacity-50">
                <Sparkles size={15} /> {genFollow ? t("e4.generating") : t("e5.followupCta")}
              </button>
            </div>
          )}

          {/* Status changer */}
          <div className="mt-5">
            <label className="mb-1.5 block text-xs font-semibold text-[#4A6358]">{t("caseDetail.updateStatus")}</label>
            <Select value={caseData.status} onValueChange={updateStatus}>
              <SelectTrigger data-testid="case-detail-status-select" className="h-12 rounded-xl bg-white">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {CASE_STATUSES.map((s) => (
                  <SelectItem key={s} value={s}>{t(`status.${s}`)}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {caseData.notes && (
            <div className="mt-5 rounded-2xl border border-[#E2EAE5] bg-white p-4">
              <h3 className="mb-1 text-xs font-semibold uppercase tracking-wide text-[#4A6358]">{t("caseDetail.notes")}</h3>
              <p className="whitespace-pre-wrap text-sm text-[#1A2E26]">{caseData.notes}</p>
            </div>
          )}

          {/* Key info extracted from analyzed documents */}
          {caseData.key_info?.length > 0 && (
            <div className="mt-6" data-testid="case-key-info">
              <h3 className="mb-3 font-display text-sm font-bold uppercase tracking-wide text-[#4A6358]">
                {t("analysis.criticalInfo")}
              </h3>
              <div className="space-y-2">
                {caseData.key_info.map((ki, i) => {
                  const Icon = KI_ICON[ki.category] || FileText;
                  const st = KI_STATUS[ki.status] || KI_STATUS.PENDING;
                  const stLabel =
                    ki.status === "CONFIRMED"
                      ? t("analysis.confirmed")
                      : ki.status === "CORRECTED"
                      ? t("analysis.corrected")
                      : t("analysis.pending");
                  return (
                    <button
                      key={i}
                      data-testid={`case-keyinfo-${i}`}
                      onClick={() => navigate(`/documents/${ki.document_id}/analysis`)}
                      className="flex w-full items-center gap-3 rounded-2xl border border-[#E2EAE5] bg-white p-3 text-left transition-all hover:border-[#C4DFD2] active:scale-[0.99]"
                    >
                      <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-[#F0F4F1] text-[#2D5A4C]">
                        <Icon size={16} />
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="text-[10px] font-bold uppercase tracking-wide text-[#7A9488]">{t(`analysis.cat.${ki.category}`)}</p>
                        <p
                          className="overflow-hidden text-sm font-semibold text-[#1A2E26] [display:-webkit-box] [-webkit-box-orient:vertical] [-webkit-line-clamp:2]"
                          dir="auto"
                        >
                          {ki.value}
                        </p>
                        {(ki.source_label || ki.filename) && <p className="mt-0.5 truncate text-[10px] text-[#B0C2B8]">{ki.source_label || ki.filename}</p>}
                      </div>
                      <span className="shrink-0 rounded-full px-2 py-0.5 text-[10px] font-bold" style={{ backgroundColor: st.bg, color: st.c }}>
                        {stLabel}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Documents */}
          <div className="mt-6">
            <h3 className="mb-3 font-display text-sm font-bold uppercase tracking-wide text-[#4A6358]">
              {t("caseDetail.documents")}
            </h3>
            {(!caseData.documents || caseData.documents.length === 0) ? (
              <p className="rounded-xl border border-[#E2EAE5] bg-white/60 px-3 py-4 text-center text-xs text-[#B0C2B8]">
                {t("caseDetail.noDocuments")}
              </p>
            ) : (
              <div className="space-y-2">
                {caseData.documents.map((d) => {
                  const isPdf = d.content_type === "application/pdf";
                  return (
                    <div
                      key={d.id}
                      className="flex w-full items-center gap-3 rounded-2xl border border-[#E2EAE5] bg-white p-3 transition-all hover:border-[#C4DFD2]"
                    >
                      <button
                        data-testid={`case-doc-${d.id}`}
                        onClick={() => setPreview(d)}
                        className="flex min-w-0 flex-1 items-center gap-3 text-left active:scale-[0.99]"
                      >
                        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[#F0F4F1] text-[#2D5A4C]">
                          {isPdf ? <FileText size={18} /> : <ImageIcon size={18} />}
                        </div>
                        <span className="min-w-0 flex-1 truncate text-sm font-semibold text-[#1A2E26]">{d.title_localized || d.original_filename}</span>
                        <Eye size={17} className="shrink-0 text-[#7A9488]" />
                      </button>
                      <button
                        data-testid={`case-doc-analyze-${d.id}`}
                        onClick={() => navigate(`/documents/${d.id}/analysis`)}
                        className="rounded-full p-2 text-[#2D5A4C] transition-colors hover:bg-[#F0F4F1] active:scale-95"
                        aria-label="analyze"
                      >
                        <Sparkles size={17} />
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          <CaseWorkspace caseData={caseData} />

          <ResolutionAssistant caseData={caseData} initialTab={initialTab} />

          <CaseChat caseId={caseData.id} />

          {/* Archive / Delete actions */}
          <div className="mt-8 grid grid-cols-2 gap-3 border-t border-[#F0F4F1] pt-6">
            <button
              data-testid="case-archive-button"
              onClick={archiveCase}
              disabled={caseData.status === "ARCHIVED"}
              className="flex items-center justify-center gap-2 rounded-xl border border-[#E2EAE5] bg-white py-3 text-sm font-semibold text-[#4A6358] transition-colors hover:bg-[#F0F4F1] active:scale-[0.99] disabled:opacity-40"
            >
              <Archive size={16} /> {t("caseDetail.archive")}
            </button>
            <button
              data-testid="case-delete-button"
              onClick={() => setConfirmDelete(true)}
              className="flex items-center justify-center gap-2 rounded-xl border border-[#F3D0CB] bg-[#FDF2F0] py-3 text-sm font-semibold text-[#C03A2B] transition-colors hover:bg-[#FBE6E2] active:scale-[0.99]"
            >
              <Trash2 size={16} /> {t("caseDetail.delete")}
            </button>
          </div>
        </main>
      )}

      <AlertDialog open={confirmDelete} onOpenChange={setConfirmDelete}>
        <AlertDialogContent data-testid="case-delete-dialog" className="max-w-[340px] rounded-2xl">
          <AlertDialogHeader>
            <AlertDialogTitle>{t("caseDetail.deleteConfirmTitle")}</AlertDialogTitle>
            <AlertDialogDescription>
              {t("caseDetail.deleteConfirmText").replace("{name}", caseData?.title || "")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="case-delete-cancel" disabled={deleting}>{t("common.cancel")}</AlertDialogCancel>
            <AlertDialogAction
              data-testid="case-delete-confirm"
              onClick={(e) => { e.preventDefault(); deleteCase(); }}
              disabled={deleting}
              className="bg-[#C03A2B] hover:bg-[#A82E20]"
            >
              {t("caseDetail.delete")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog open={!!preview} onOpenChange={(o) => !o && setPreview(null)}>
        <DialogContent data-testid="document-preview-dialog" className="max-w-md p-0">
          {preview && <DocumentPreview doc={preview} />}
        </DialogContent>
      </Dialog>
      <BottomNav />
    </div>
  );
}
